import React from "react";

import { useTranslation } from "react-i18next";

import { OverUnder } from "@data-types/sport-stats/SportStatsTypes";
import RecentGames from "../RecentGames";

import Graph from "../Graph";

export default function OverUnderStatistics(props: {
	homeTeam: OverUnder["data"];
	awayTeam: OverUnder["data"];
}) {
	const { t } = useTranslation();

	const homeTeamThresholds = props.homeTeam?.overUnderData?.length
		? props.homeTeam?.overUnderData?.map((data) =>
				data ? data.threshold : ""
			)
		: [];
	const awayTeamThresholds = props.awayTeam?.overUnderData?.length
		? props.awayTeam?.overUnderData?.map((data) =>
				data ? data.threshold : ""
			)
		: [];

	const availableThresholds = new Set([
		...homeTeamThresholds,
		...awayTeamThresholds,
	]);

	const thresholds: {
		homeTeam: number;
		awayTeam: number;
		threshold: string;
	}[] = [];

	availableThresholds.forEach((threshold) => {
		let homeTeamInfo = null;
		let awayTeamInfo = null;
		const parsedValue = parseFloat(threshold || "");
		// Check if the value is positive and less than or equal to 2.5
		if (isNaN(parsedValue) || parsedValue < 0 || parsedValue > 2.5) {
			return;
		}

		if (props.homeTeam?.overUnderData?.length) {
			homeTeamInfo = props.homeTeam.overUnderData.find(
				(item) => item && item.threshold === threshold
			);
		}
		if (props.awayTeam?.overUnderData?.length) {
			awayTeamInfo = props.awayTeam.overUnderData.find(
				(item) => item && item.threshold === threshold
			);
		}
		thresholds.push({
			homeTeam: homeTeamInfo ? homeTeamInfo.frequency || 0 : 0,
			awayTeam: awayTeamInfo ? awayTeamInfo.frequency || 0 : 0,
			threshold: threshold || "+" + parsedValue,
		});
	});

	return (
		<>
			<RecentGames
				homeTeamEventCount={props.homeTeam?.eventCount}
				awayTeamEventCount={props.awayTeam?.eventCount}
			/>

			<div
				className="card card--raised card--raised--framed card--stats"
				data-at="at-OverUnderStatistics__comb"
			>
				<div className="card__header card--stats__header">
					<div className="card__title card__title--base card--stats__heading">
						{t(
							"EVENT.STATISTICS.BETTING_TYPE_STATS.COMBINED_AV_GOALS"
						)}
					</div>
				</div>

				<div className="card--stats__body">
					<Graph
						graphName={t("EVENT.STATISTICS.TOTAL")}
						data={{
							homeValue: props.homeTeam?.avgMatchGoals,
							awayValue: props.awayTeam?.avgMatchGoals,
						}}
						isTotalGraph
						secondaryInfo
						removeValues
						noBorder
					/>

					<Graph
						graphName={t(
							"EVENT.STATISTICS.BETTING_TYPE_STATS.SCORED"
						)}
						data={{
							homeValue: props.homeTeam?.avgGoalsScored,
							awayValue: props.awayTeam?.avgGoalsScored,
						}}
						secondaryInfo
						removeValues
						maxHome={props.homeTeam?.avgMatchGoals}
						maxAway={props.awayTeam?.avgMatchGoals}
						noBorder
					/>

					<Graph
						graphName={t(
							"EVENT.STATISTICS.BETTING_TYPE_STATS.CONCEDED"
						)}
						data={{
							homeValue: props.homeTeam?.avgGoalsConceded,
							awayValue: props.awayTeam?.avgGoalsConceded,
						}}
						secondaryInfo
						removeValues
						maxHome={props.homeTeam?.avgMatchGoals}
						maxAway={props.awayTeam?.avgMatchGoals}
						noBorder
					/>
				</div>
			</div>

			{thresholds?.length > 0 && (
				<div
					className="card card--raised card--raised--framed card--stats"
					data-at="at-OverUnderStatistics__freq"
				>
					<div className="card__header card--stats__header">
						<div className="card__title card__title--base card--stats__heading">
							{t("EVENT.STATISTICS.BETTING_TYPE_STATS.FREQUENCY")}
						</div>
					</div>

					<div className="card--stats__body">
						{thresholds.map((data, index) => (
							<Graph
								graphName={data.threshold}
								data={{
									homeValue: data.homeTeam,
									awayValue: data.awayTeam,
								}}
								maxHome={100}
								maxAway={100}
								isPercentage
								secondaryInfo
								removeValues
								home={t("EVENT.OFFER.COMPETITOR_HOME")}
								away={t("EVENT.OFFER.COMPETITOR_AWAY")}
								isNotUnique={index > 0}
								noBorder
							/>
						))}
					</div>
				</div>
			)}
		</>
	);
}
